import {
  actionToIntlTitle,
  EPermissionGrantActions,
  EPermissionGrantSubjects,
  EPermissionSpecialAuthorization,
  specialAuthorizationToIntlTitle,
  subjectToIntlTitle,
  TPermission,
  TPermissionGrant,
} from './permissions.type'
import { WithPermission } from 'src/providers/current-project.provider'
import { useTranslation } from 'react-i18next'
import { ReactNode } from 'react'
import {
  CurecallBadge,
  ECurecallBadgeColor,
  CurecallButton,
  CurecallTooltip,
  ECurecallSize,
  PenIcon,
  TrashIcon,
} from '@honestica/curecall-ui'

export const TextCell = ({ children }: { children: ReactNode }) => (
  <p className="c-font-nexa c-text-xs c-m-0">{children}</p>
)

export const SpecialAuthorizationCell = ({
  specialAuthorizations,
}: {
  specialAuthorizations: Array<EPermissionSpecialAuthorization>
}) => {
  const { t } = useTranslation()
  return (
    <div className="c-flex">
      {specialAuthorizations.map((authorization, index) => (
        <CurecallBadge
          color={
            authorization === EPermissionSpecialAuthorization.CAN_VIEW_PATIENT_IDENTITIES
              ? ECurecallBadgeColor.GREEN
              : ECurecallBadgeColor.OUTLINED_PRIMARY
          }
          key={index}
        >
          {t(specialAuthorizationToIntlTitle[authorization])}
        </CurecallBadge>
      ))}
    </div>
  )
}

export const GrantsCell = ({ grants }: { grants: TPermissionGrant[] }) => {
  const { t } = useTranslation()
  const permissionToColor = {
    [EPermissionGrantActions.GET]: ECurecallBadgeColor.SMOOTH_SECONDARY,
    [EPermissionGrantActions.CREATE]: ECurecallBadgeColor.SMOOTH_GREEN,
    [EPermissionGrantActions.UPDATE]: ECurecallBadgeColor.RED,
    [EPermissionGrantActions.DELETE]: ECurecallBadgeColor.GREY,
    [EPermissionGrantActions.ALL]: ECurecallBadgeColor.SMOOTH_PRIMARY,
  }

  return (
    <div className="c-flex c-flex-col">
      {grants?.length > 0 ? (
        grants
          .sort((g1, g2) => (g1?.subject > g2?.subject ? 1 : 0))
          .map((grant, index) => (
            <div className="c-flex c-mb-1 c-relative" key={index}>
              <div className="c-w-72 c-relative">
                <div className="c-absolute c-left-0 c-right-0 c-h-1/2 c-border-b c-border-black c-border-dashed c-z-0" />
                <span className="c-z-10 c-relative c-pr-1 c-bg-white">
                  {grant?.subject ? t(subjectToIntlTitle[grant?.subject]) : ''}
                </span>
              </div>
              <div className="c-flex">
                {grant?.actions.map((action, actionIndex) => (
                  <CurecallBadge key={actionIndex} className="c-ml-1" color={permissionToColor[action]}>
                    {t(actionToIntlTitle[action])}
                  </CurecallBadge>
                ))}
              </div>
            </div>
          ))
      ) : (
        <p className="c-font-nexa c-font-bold c-italic">
          {t('pages.configuration.permissions.empty-grants')}
        </p>
      )}
    </div>
  )
}

export const ActionsCell = ({
  permission,
  onDelete,
  onUpdate,
  disableDelete,
}: {
  permission: TPermission
  onDelete: VoidFunction
  onUpdate: (id: number) => void
  disableDelete: boolean
}) => {
  const { t } = useTranslation()
  return (
    <div className="c-flex c-gap-2">
      <WithPermission
        requiredSubject={EPermissionGrantSubjects.USER_MANAGEMENT}
        requiredAction={EPermissionGrantActions.UPDATE}
      >
        <CurecallButton
          isGhostButton={true}
          ariaLabelText={t('pages.configuration.permissions.update-permission', {
            permissionName: permission.name,
          })}
          iconType={<PenIcon />}
          onClick={() => onUpdate(permission.id)}
          size={ECurecallSize.SMALL}
          className="tw-text-primary"
        />
      </WithPermission>

      {disableDelete ? (
        <CurecallTooltip
          content={t('components.permissions.cannot-delete-current-permission-tooltip')}
          tooltipPosition={'left'}
          wrapperClassName="tw-items-center tw-justify-center tw-h-7 tw-w-7"
        >
          <CurecallButton
            ariaLabelText={t('pages.configuration.permissions.delete-permission', {
              permissionName: permission.name,
            })}
            iconType={<TrashIcon className="c-text-grey-600" />}
            size={ECurecallSize.SMALL}
            isDisabled={true}
            disabled={true}
          />
        </CurecallTooltip>
      ) : (
        <WithPermission
          requiredSubject={EPermissionGrantSubjects.USER_MANAGEMENT}
          requiredAction={EPermissionGrantActions.DELETE}
        >
          <CurecallButton
            isGhostButton={true}
            ariaLabelText={t('pages.configuration.permissions.delete-permission', {
              permissionName: permission.name,
            })}
            iconType={<TrashIcon />}
            onClick={onDelete}
            size={ECurecallSize.SMALL}
            className="tw-text-grey-700"
          />
        </WithPermission>
      )}
    </div>
  )
}
